<template>
  <div class="users-list">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex lg12>
          <div class="users-hero">
            <p class="users-hero__crumb">Administración / Usuarios</p>
            <h1 class="users-hero__title">Gestión de Usuarios</h1>
            <p class="users-hero__subtitle">
              Administra cuentas, rol principal y acceso al sistema desde un
              solo panel.
            </p>
          </div>
        </v-flex>

        <v-flex lg12>
          <v-card class="users-panel">
            <v-toolbar card flat class="users-toolbar">
              <v-text-field
                clearable
                flat
                hide-details
                placeholder="Buscar usuario..."
                solo
                class="users-search"
                v-model="search"
              ></v-text-field>
              <v-btn icon class="toolbar-icon-btn" @click="loadUsers">
                <v-icon>search</v-icon>
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn @click="openDialog()" class="btn-primary" dark>
                <v-icon left>person_add</v-icon>Nuevo Usuario
              </v-btn>
            </v-toolbar>

            <v-divider></v-divider>

            <v-card-text class="pa-0 users-content">
              <v-data-table
                v-if="!$vuetify.breakpoint.smAndDown"
                class="users-table"
                :headers="headers"
                :items="users"
                :loading="loading"
                hide-actions
                no-data-text="No hay usuarios disponibles"
              >
                <template slot="items" slot-scope="props">
                  <td>{{ props.item.firstName }}</td>
                  <td>{{ props.item.lastName }}</td>
                  <td>{{ props.item.email }}</td>
                  <td>{{ roleName(props.item) }}</td>
                  <td>
                    <v-chip
                      :class="
                        props.item.enabled
                          ? 'status-chip status-chip--active'
                          : 'status-chip status-chip--inactive'
                      "
                      text-color="white"
                      small
                    >
                      {{ props.item.enabled ? 'Activo' : 'Inactivo' }}
                    </v-chip>
                  </td>
                  <td>
                    <v-btn
                      @click="openDialog(props.item)"
                      class="action-btn action-btn--edit"
                      flat
                      icon
                      small
                    >
                      <v-icon>edit</v-icon>
                    </v-btn>
                    <v-btn
                      @click="openRolesDialog(props.item)"
                      class="action-btn action-btn--roles"
                      flat
                      icon
                      small
                      title="Asignar roles"
                    >
                      <v-icon>assignment</v-icon>
                    </v-btn>
                    <v-btn
                      @click="disableUser(props.item.id)"
                      class="action-btn action-btn--delete"
                      flat
                      icon
                      small
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </td>
                </template>
              </v-data-table>

              <div v-else class="users-mobile-list">
                <v-container fluid class="pa-3">
                  <v-layout row wrap>
                    <v-flex xs12 v-if="!users.length && !loading">
                      <v-alert type="info" value="true" class="mb-0">
                        No hay usuarios disponibles.
                      </v-alert>
                    </v-flex>

                    <v-flex
                      xs12
                      v-for="user in users"
                      :key="user.id"
                      class="mb-3"
                    >
                      <v-card class="user-mobile-card">
                        <div class="user-mobile-card__head">
                          <div>
                            <p class="user-mobile-card__title">
                              {{ user.firstName }} {{ user.lastName }}
                            </p>
                            <p class="user-mobile-card__email">
                              {{ user.email }}
                            </p>
                          </div>
                          <v-chip
                            :class="
                              user.enabled
                                ? 'status-chip status-chip--active'
                                : 'status-chip status-chip--inactive'
                            "
                            text-color="white"
                            small
                          >
                            {{ user.enabled ? 'Activo' : 'Inactivo' }}
                          </v-chip>
                        </div>

                        <div class="user-mobile-card__body">
                          <div class="info-row">
                            <span class="label">Rol</span>
                            <span class="value">{{ roleName(user) }}</span>
                          </div>
                        </div>

                        <div class="user-mobile-card__actions">
                          <v-btn
                            small
                            dark
                            class="mobile-action mobile-action--edit"
                            @click="openDialog(user)"
                          >
                            <v-icon small left>edit</v-icon>Editar
                          </v-btn>
                          <v-btn
                            small
                            dark
                            class="mobile-action mobile-action--roles"
                            @click="openRolesDialog(user)"
                          >
                            <v-icon small left>assignment</v-icon>Roles
                          </v-btn>
                          <v-btn
                            small
                            dark
                            class="mobile-action mobile-action--delete"
                            @click="disableUser(user.id)"
                          >
                            <v-icon small left>delete</v-icon>Desactivar
                          </v-btn>
                        </div>
                      </v-card>
                    </v-flex>
                  </v-layout>
                </v-container>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="680" persistent v-model="dialog">
      <v-card class="users-dialog users-dialog--main">
        <v-card-title class="users-dialog__title">
          {{ editingUser.id ? 'Editar Usuario' : 'Nuevo Usuario' }}
        </v-card-title>

        <v-card-text class="users-dialog__content">
          <v-form ref="form">
            <v-layout row wrap>
              <v-flex xs12 md6>
                <v-text-field
                  v-model="editingUser.firstName"
                  label="Nombre"
                  required
                  :rules="[v => !!v || 'El nombre es requerido']"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md6>
                <v-text-field
                  v-model="editingUser.lastName"
                  label="Apellido"
                  required
                  :rules="[v => !!v || 'El apellido es requerido']"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="editingUser.email"
                  label="Email"
                  type="email"
                  required
                  :rules="[
                    v => !!v || 'El email es requerido',
                    v => /.+@.+\..+/.test(v) || 'Email inválido',
                  ]"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 v-if="!editingUser.id">
                <v-text-field
                  v-model="editingUser.password"
                  label="Contraseña"
                  type="password"
                  required
                  :rules="[v => !!v || 'La contraseña es requerida']"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md7>
                <v-select
                  v-model="editingUser.roleId"
                  :items="availableRoles"
                  item-text="name"
                  item-value="id"
                  label="Rol Principal"
                  placeholder="Selecciona un rol"
                ></v-select>
              </v-flex>
              <v-flex xs12 md5 class="d-flex align-center">
                <v-checkbox
                  v-model="editingUser.enabled"
                  label="Activo"
                  class="mt-3"
                ></v-checkbox>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-card-actions class="users-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn @click="dialog = false" class="btn-cancel" text
            >Cancelar</v-btn
          >
          <v-btn @click="saveUser" class="btn-primary" dark :loading="saving">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="620" persistent v-model="rolesDialog">
      <v-card class="users-dialog users-dialog--roles">
        <v-card-title class="users-dialog__title">
          Asignar Roles a: {{ selectedUser.firstName }}
          {{ selectedUser.lastName }}
        </v-card-title>

        <v-card-text class="users-dialog__content">
          <v-list class="role-list">
            <v-list-item-group v-model="selectedRoleIds" multiple>
              <v-list-item
                v-for="role in availableRoles"
                :key="role.id"
                :value="role.id"
                class="role-list__item"
              >
                <template v-slot:default="{ active }">
                  <v-checkbox
                    :input-value="active"
                    color="primary"
                  ></v-checkbox>
                  <v-list-item-title>{{ role.name }}</v-list-item-title>
                </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>

        <v-card-actions class="users-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn @click="rolesDialog = false" class="btn-cancel" text
            >Cancelar</v-btn
          >
          <v-btn
            @click="assignRoles"
            class="btn-primary"
            dark
            :loading="saving"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="350" persistent v-model="loading">
      <v-card class="loading-card">
        <v-card-text class="text-center py-4">
          <v-progress-circular
            color="primary"
            indeterminate
          ></v-progress-circular>
          <p class="mt-2 mb-0">Cargando...</p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/api/index'

export default {
  data() {
    return {
      users: [],
      availableRoles: [],
      search: '',
      dialog: false,
      rolesDialog: false,
      loading: false,
      saving: false,
      headers: [
        { text: 'Nombre', value: 'firstName' },
        { text: 'Apellido', value: 'lastName' },
        { text: 'Email', value: 'email' },
        { text: 'Rol', value: 'role.name' },
        { text: 'Estado', value: 'enabled' },
        { text: 'Acciones', sortable: false },
      ],
      editingUser: {
        id: null,
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        roleId: null,
        enabled: true,
      },
      selectedUser: {},
      selectedRoleIds: [],
    }
  },
  mounted() {
    this.loadUsers()
    this.loadRoles()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const response = await Api.User.getAll({ term: this.search })
        this.users = response.data || response
      } catch (error) {
        this.$notify({
          group: 'foo',
          title: 'Error',
          text: 'Error al cargar los usuarios',
          type: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    async loadRoles() {
      try {
        const response = await Api.Role.getAll()
        this.availableRoles = response.data || response || []
      } catch (error) {
        console.error('Error loading roles', error)
      }
    },
    openDialog(user = null) {
      if (user) {
        this.editingUser = { ...user }
      } else {
        this.editingUser = {
          id: null,
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          roleId: null,
          enabled: true,
        }
      }
      this.dialog = true
    },
    async openRolesDialog(user) {
      this.selectedUser = user
      this.saving = true
      try {
        const response = await Api.User.getUserRoles(user.id)
        const roles = response.data || response || []
        this.selectedRoleIds = roles.map(r => r.id)
      } catch (error) {
        this.selectedRoleIds = []
      } finally {
        this.saving = false
      }
      this.rolesDialog = true
    },
    async saveUser() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      try {
        await Api.User.save(this.editingUser)
        this.$notify({
          group: 'foo',
          title: 'Éxito',
          text: 'Usuario guardado correctamente',
          type: 'success',
        })
        this.dialog = false
        this.loadUsers()
      } catch (error) {
        this.$notify({
          group: 'foo',
          title: 'Error',
          text: 'Error al guardar el usuario',
          type: 'error',
        })
      } finally {
        this.saving = false
      }
    },
    async disableUser(id) {
      if (!confirm('¿Estás seguro de que deseas desactivar este usuario?'))
        return

      try {
        await Api.User.disable({ id })
        this.$notify({
          group: 'foo',
          title: 'Éxito',
          text: 'Usuario desactivado correctamente',
          type: 'success',
        })
        this.loadUsers()
      } catch (error) {
        this.$notify({
          group: 'foo',
          title: 'Error',
          text: 'Error al desactivar el usuario',
          type: 'error',
        })
      }
    },
    async assignRoles() {
      this.saving = true
      try {
        await Api.User.assignRoles(this.selectedUser.id, this.selectedRoleIds)
        this.$notify({
          group: 'foo',
          title: 'Éxito',
          text: 'Roles asignados correctamente',
          type: 'success',
        })
        this.rolesDialog = false
        this.loadUsers()
      } catch (error) {
        this.$notify({
          group: 'foo',
          title: 'Error',
          text: 'Error al asignar roles',
          type: 'error',
        })
      } finally {
        this.saving = false
      }
    },
    roleName(user) {
      return user && user.role ? user.role.name : '---'
    },
  },
}
</script>

<style scoped>
.users-list {
  --brand-primary: #e5252a;
  --brand-secondary: #254f87;
  --brand-accent: #c79a2b;
  --surface-dark: #0f1728;
  --surface-dark-soft: #111d34;
  --line-soft: rgba(255, 255, 255, 0.11);
  --text-strong: #f2f6ff;
  --text-soft: #b8c4da;
  min-height: 100%;
  background: radial-gradient(
      circle at 15% 12%,
      rgba(37, 79, 135, 0.35) 0%,
      rgba(37, 79, 135, 0) 45%
    ),
    radial-gradient(
      circle at 82% 20%,
      rgba(229, 37, 42, 0.2) 0%,
      rgba(229, 37, 42, 0) 38%
    ),
    linear-gradient(180deg, #0a1221 0%, #0e182d 100%);
  padding-bottom: 16px;
}

.users-hero {
  border-radius: 14px;
  border: 1px solid var(--line-soft);
  background: linear-gradient(
    90deg,
    rgba(16, 30, 54, 0.88),
    rgba(11, 21, 39, 0.88)
  );
  padding: 22px 24px;
  margin-bottom: 14px;
}

.users-hero__crumb {
  margin: 0;
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 600;
}

.users-hero__title {
  margin: 8px 0 0;
  color: #ffffff;
  font-size: 30px;
  line-height: 1.1;
}

.users-hero__subtitle {
  margin: 10px 0 0;
  color: var(--text-soft);
  font-size: 14px;
}

.users-panel {
  border-radius: 14px;
  border: 1px solid var(--line-soft);
  overflow: hidden;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 40, 0.95) 0%,
    rgba(11, 18, 31, 0.96) 100%
  );
}

.users-toolbar {
  background: transparent !important;
  border-bottom: 1px solid var(--line-soft);
  padding: 10px 12px;
}

.users-search {
  max-width: 360px;
}

.users-search ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.users-search ::v-deep input {
  color: #e9eefc !important;
}

.users-search ::v-deep .v-input__slot input::placeholder {
  color: #9fb0cf !important;
}

.toolbar-icon-btn {
  color: var(--brand-accent) !important;
}

.btn-primary {
  background: linear-gradient(90deg, #e5252a 0%, #c11e23 100%) !important;
  border-radius: 10px;
  box-shadow: 0 10px 20px rgba(229, 37, 42, 0.22);
}

.users-content {
  background: transparent;
}

.users-table {
  background: transparent;
}

.users-table ::v-deep table {
  background: transparent;
}

.users-table ::v-deep thead th {
  color: #89a0c6 !important;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--line-soft) !important;
}

.users-table ::v-deep tbody td {
  color: var(--text-strong);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}

.status-chip {
  font-weight: 700;
}

.status-chip--active {
  background-color: #1f9d63 !important;
}

.status-chip--inactive {
  background-color: #d84d4d !important;
}

.action-btn {
  margin: 0 2px;
}

.action-btn--edit {
  color: #5fa8ff !important;
}

.action-btn--roles {
  color: var(--brand-accent) !important;
}

.action-btn--delete {
  color: var(--brand-primary) !important;
}

.users-mobile-list {
  background: transparent;
}

.user-mobile-card {
  border-radius: 12px;
  border: 1px solid var(--line-soft);
  background: linear-gradient(
    180deg,
    rgba(16, 28, 49, 0.9),
    rgba(12, 22, 39, 0.92)
  );
}

.user-mobile-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 14px 10px;
}

.user-mobile-card__title {
  margin: 0;
  color: #f5f8ff;
  font-size: 15px;
  font-weight: 700;
}

.user-mobile-card__email {
  margin: 3px 0 0;
  color: #a8bbda;
  font-size: 12px;
}

.user-mobile-card__body {
  padding: 0 14px 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.info-row .label {
  color: #9bb0d1;
  font-size: 12px;
}

.info-row .value {
  color: #eef3ff;
  font-size: 13px;
  font-weight: 600;
}

.user-mobile-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 12px 12px;
}

.mobile-action {
  border-radius: 8px;
}

.mobile-action--edit {
  background: #254f87 !important;
}

.mobile-action--roles {
  background: #c79a2b !important;
  color: #1b1f29 !important;
}

.mobile-action--delete {
  background: #e5252a !important;
}

.users-dialog {
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    rgba(16, 28, 49, 0.97),
    rgba(12, 22, 39, 0.98)
  ) !important;
  border: 1px solid var(--line-soft);
}

.users-dialog__title {
  color: #ffffff;
  font-weight: 700;
  font-size: 21px;
  border-bottom: 1px solid var(--line-soft);
  background: linear-gradient(
    90deg,
    rgba(37, 79, 135, 0.25),
    rgba(229, 37, 42, 0.18)
  );
}

.users-dialog__content {
  padding-top: 16px;
}

.users-dialog__content ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.04) !important;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.users-dialog__content ::v-deep .v-label,
.users-dialog__content ::v-deep input,
.users-dialog__content ::v-deep .v-select__selection,
.users-dialog__content
  ::v-deep
  .v-input--selection-controls__ripple
  + .v-label {
  color: #e7efff !important;
}

.users-dialog__actions {
  border-top: 1px solid var(--line-soft);
}

.btn-cancel {
  color: #aec0de !important;
}

.role-list {
  background: transparent !important;
}

.role-list__item {
  border-radius: 8px;
  margin-bottom: 6px;
}

.role-list__item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.role-list__item ::v-deep .v-list-item__title {
  color: #e8f0ff;
}

.loading-card {
  border-radius: 12px;
}

@media (max-width: 960px) {
  .users-hero {
    padding: 16px;
  }

  .users-hero__title {
    font-size: 24px;
  }

  .users-toolbar {
    padding: 8px;
  }

  .users-search {
    max-width: none;
  }

  .btn-primary {
    min-width: 0;
    padding: 0 12px;
  }
}
</style>
